import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { BoxProps } from '@opendoor/bricks-next';
import { Col, Row, Text } from '@opendoor/bricks-next';
import { IconChevronDown } from '@tabler/icons-react';

import { BOX_SHADOW_Z2 } from 'shared/helpers/constants';
import { HOMEPAGE_URL } from 'shared/helpers/globals';
import { ACCOUNT_URL } from 'shared/helpers/routes';
import { useOnClickOutside } from 'shared/hooks/useOnClickOutside';

import { useAuthWithClientSideFallback } from 'platform/auth-fe';
import { ExternalLink, Link, PressableBox } from 'platform/bricks-next';
import { getLoginUrl } from 'platform/loginRedirect';
import { ClientOnly } from 'platform/remix-run/utils/client-only';

type AuthNavBarWidgetProps = {
  label?: string | null;
  additionalOptions?: NavMenuItem[];
  hideDefaultOptions?: boolean;
};

/**
 * The auth/account/user widget that lives at the top-right of the nav bar.
 */
export const AuthNavBarWidget: FC<AuthNavBarWidgetProps> = (props) => {
  const { state: authenticationState } = useAuthWithClientSideFallback();

  return (
    <Col
      minWidth={100}
      alignItems="flex-end"
      opacity={authenticationState === 'authenticating' ? 0 : 1}
      $platform-web={{ transition: 'all ease 0.5s' }}
    >
      <NavMenu {...props} />
    </Col>
  );
};

const NavMenu: FC<AuthNavBarWidgetProps> = ({
  label,
  additionalOptions = [],
  hideDefaultOptions = false,
}) => {
  /** Uses client-side fallback so we can render the auth state in the root level error bounary */
  const { state, user, logout } = useAuthWithClientSideFallback();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollBoxRef = useRef<HTMLDivElement | null>(null);
  useOnClickOutside(containerRef, () => setIsMenuOpen(false));

  const defaultItems = useDefaultNavMenuItems(logout);
  const items = useMemo(() => {
    const items = [...additionalOptions];

    if (!hideDefaultOptions) {
      items.push(...defaultItems);
    }

    return items;
  }, [additionalOptions, defaultItems, hideDefaultOptions]);

  // this is a hack because color is "not allowed" on Boxes.
  const chevronHoverProps = useMemo<BoxProps>(
    () => ({ '$group-hover': { color: '$contentStateHoverPrimary' } } as BoxProps),
    [],
  );

  useEffect(() => {
    if (!isMenuOpen) {
      // delay scroll reset until the menu is completely transparent (must match transition in AuthNavBarWidget)
      const timeout = setTimeout(() => scrollBoxRef.current?.scrollTo({ top: 0 }), 500);
      return () => clearTimeout(timeout);
    }
  }, [isMenuOpen]);

  if (state === 'authenticating') {
    return null;
  } else if (state !== 'authenticated') {
    // Show link to login page in both not_authenticated and error states.
    // Arguable whether this is the right thing to show on error.
    return (
      <ClientOnly>
        {() => (
          <ExternalLink
            href={getLoginUrl(window.location.href)}
            analyticsName="cake-auth-nav-bar-sign-in"
            aria-label="Navigate to the login page"
          >
            Sign in
          </ExternalLink>
        )}
      </ClientOnly>
    );
  }

  // Include the Welcome text as part of the outside click alerter
  // so that the menu can be closed by clicking the Welcome text again.
  return (
    <Col flex={1} justifyContent="center" ref={containerRef}>
      <PressableBox
        id="auth-nav-bar-button"
        alignItems="center"
        onPress={() => setIsMenuOpen((prev) => !prev)}
        aria-label="Toggle account menu"
        analyticsName="auth-nav-bar-toggle"
        gap="$4x"
        group
        containerType="inherit"
      >
        <Text display="flex" tag="p" typography="$labelMedium" mr="$1x">
          {label ?? `Welcome ${user?.firstName}`}
        </Text>
        <Row
          {...chevronHoverProps}
          $platform-web={{ color: '$contentStateInactivePrimary', transition: 'all ease 0.3s' }}
        >
          <IconChevronDown size={20} />
        </Row>
      </PressableBox>
      {/* outermost Col for more convenient absolute positioning */}
      <Col>
        {/* menu positioning and visibility */}
        <Col
          className="auth-nav-dropdown"
          position="absolute"
          zIndex={1}
          top="$4x"
          right={0}
          $platform-web={{
            transition: 'all ease 0.3s',
            visibility: isMenuOpen ? 'visible' : 'hidden',
          }}
          opacity={isMenuOpen ? 1 : 0}
        >
          {/* sizing and content clipping w/rounded corners */}
          <Col
            width="max-content"
            maxHeight="$180x"
            overflow="hidden"
            borderRadius="$4x"
            backgroundColor="$backgroundPrimary"
            $platform-web={{
              boxShadow: BOX_SHADOW_Z2,
            }}
          >
            {/* scrollable content */}
            <Col
              ref={scrollBoxRef}
              flex={1}
              py="$6x"
              $platform-web={{
                overflowY: 'auto',
              }}
            >
              {items.map((item) => (
                <NavMenuItem key={item.analyticsName} {...item} />
              ))}
            </Col>
          </Col>
        </Col>
      </Col>
    </Col>
  );
};

type NavMenuItem = NavMenuLink | NavMenuAction;

const NavMenuItem: FC<NavMenuItem> = (props) =>
  itemIsLink(props) ? <NavMenuLinkComponent {...props} /> : <NavMenuActionComponent {...props} />;

const useDefaultNavMenuItems = (logout: () => Promise<void>) =>
  useMemo<NavMenuItem[]>(
    () => [
      {
        label: 'Add address',
        href: `${HOMEPAGE_URL}/address-entry`,
        ariaLabel: 'Navigates to selling address entry',
        analyticsName: 'cake-auth-nav-bar-address-entry',
      },
      {
        label: 'Your account',
        href: ACCOUNT_URL,
        ariaLabel: 'Navigates to the account page',
        analyticsName: 'cake-auth-nav-bar-account',
      },
      {
        label: 'Sign out',
        action: logout,
        ariaLabel: 'Sign out of your account',
        analyticsName: 'cake-auth-nav-bar-sign-out',
      },
    ],
    [logout],
  );

const itemIsLink = (item: NavMenuItem): item is NavMenuLink => 'href' in item;

export type NavMenuLink = {
  label: string;
  href: string;
  ariaLabel: string;
  analyticsName: string;
  active?: boolean;
};

const NavMenuLinkComponent: FC<NavMenuLink> = ({
  label,
  href,
  ariaLabel,
  analyticsName,
  active,
}) => (
  <Link
    href={href}
    analyticsName={analyticsName}
    aria-label={ariaLabel}
    typography="$labelMedium"
    color={active ? '$contentStateInactivePrimary' : '$contentPrimary'}
    disabled={active}
    px="$8x"
    py="$3x"
  >
    {label}
  </Link>
);

type NavMenuAction = {
  label: string;
  action: (() => void) | (() => Promise<void>);
  ariaLabel: string;
  analyticsName: string;
  active?: boolean;
};

const NavMenuActionComponent: FC<NavMenuAction> = ({
  label,
  action,
  ariaLabel,
  analyticsName,
  active = false,
}) => (
  <PressableBox
    analyticsName={analyticsName}
    onPress={() => void action()}
    aria-label={ariaLabel}
    justifyContent="flex-start"
    px="$8x"
    py="$3x"
    disabled={active}
    group
  >
    <Text
      tag="p"
      typography="$labelMedium"
      color={active ? '$contentStateInactivePrimary' : undefined}
      $group-hover={active ? undefined : { color: '$contentStateHoverAccentBrand' }}
    >
      {label}
    </Text>
  </PressableBox>
);
